import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OUTCOME_LABELS = {
  success: 'Succès',
  'no-answer': 'Pas de réponse',
  voicemail: 'Messagerie',
  callback: 'Rappel',
  'not-interested': 'Pas intéressé',
  'wrong-number': 'Mauvais numéro',
};

const OUTCOME_CLASS = {
  success: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-500/20',
  callback: 'bg-amber-500/10 text-amber-800 dark:text-amber-400 ring-amber-500/20',
  'not-interested': 'bg-destructive/10 text-destructive ring-destructive/20',
  'no-answer': 'bg-muted text-muted-foreground ring-border',
  voicemail: 'bg-muted text-muted-foreground ring-border',
  'wrong-number': 'bg-muted text-muted-foreground ring-border',
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'Tous' },
  { value: 'success', label: OUTCOME_LABELS.success },
  { value: 'callback', label: OUTCOME_LABELS.callback },
  { value: 'not-interested', label: OUTCOME_LABELS['not-interested'] },
  { value: 'no-answer', label: OUTCOME_LABELS['no-answer'] },
  { value: 'voicemail', label: OUTCOME_LABELS.voicemail },
  { value: 'wrong-number', label: OUTCOME_LABELS['wrong-number'] },
];

const CallRecordsTable = ({ records, onContactClick }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  const itemsPerPage = 10;

  const filteredRecords =
    outcomeFilter === 'all'
      ? records
      : records?.filter((r) => r?.outcome === outcomeFilter);

  const sortedRecords = [...(filteredRecords || [])].sort((a, b) => {
    if (sortConfig.key === 'timestamp') {
      return sortConfig.direction === 'asc'
        ? new Date(a.timestamp) - new Date(b.timestamp)
        : new Date(b.timestamp) - new Date(a.timestamp);
    }
    if (sortConfig.key === 'duration') {
      return sortConfig.direction === 'asc'
        ? a.duration - b.duration
        : b.duration - a.duration;
    }
    return 0;
  });

  const paginatedRecords = sortedRecords.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const totalPages = Math.max(1, Math.ceil(sortedRecords.length / itemsPerPage));

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-foreground md:text-lg">Historique des appels</h3>
            <p className="text-sm text-muted-foreground">
              {sortedRecords.length} enregistrement{sortedRecords.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setOutcomeFilter(opt.value);
                  setCurrentPage(1);
                }}
                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                  outcomeFilter === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {paginatedRecords.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-muted-foreground">
          Aucun appel ne correspond à ce filtre.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-muted/30 text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-3 md:px-6">Contact</th>
                <th
                  className="cursor-pointer px-4 py-3 hover:text-foreground"
                  onClick={() => handleSort('timestamp')}
                >
                  <span className="inline-flex items-center gap-1">
                    Date
                    <Icon
                      name={
                        sortConfig.key === 'timestamp' && sortConfig.direction === 'asc'
                          ? 'ChevronUp'
                          : 'ChevronDown'
                      }
                      size={14}
                    />
                  </span>
                </th>
                <th
                  className="cursor-pointer px-4 py-3 hover:text-foreground"
                  onClick={() => handleSort('duration')}
                >
                  <span className="inline-flex items-center gap-1">
                    Durée
                    <Icon
                      name={
                        sortConfig.key === 'duration' && sortConfig.direction === 'asc'
                          ? 'ChevronUp'
                          : 'ChevronDown'
                      }
                      size={14}
                    />
                  </span>
                </th>
                <th className="px-4 py-3">Résultat</th>
                <th className="hidden px-4 py-3 md:table-cell md:px-6">Agent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/80">
              {paginatedRecords.map((record) => (
                <tr key={record.id} className="transition-colors hover:bg-muted/25">
                  <td className="px-4 py-3 md:px-6">
                    <button
                      type="button"
                      onClick={() => onContactClick?.(record)}
                      className="group min-w-0 text-left"
                    >
                      <p className="text-sm font-medium text-foreground group-hover:text-primary truncate max-w-[200px]">
                        {record.contactName}
                      </p>
                      {record.contactPhone ? (
                        <p className="text-xs text-muted-foreground">{record.contactPhone}</p>
                      ) : null}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-muted-foreground">
                    {formatTimestamp(record.timestamp)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium tabular-nums text-foreground">
                    {formatDuration(record.duration)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
                        OUTCOME_CLASS[record.outcome] || OUTCOME_CLASS['no-answer']
                      }`}
                    >
                      {OUTCOME_LABELS[record.outcome] || record.outcome}
                    </span>
                  </td>
                  <td className="hidden max-w-[140px] truncate px-4 py-3 text-sm text-muted-foreground md:table-cell md:px-6">
                    {record.agentName}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sortedRecords.length > itemsPerPage ? (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-border px-4 py-3 sm:flex-row md:px-6">
          <p className="text-xs text-muted-foreground">
            {(currentPage - 1) * itemsPerPage + 1}–
            {Math.min(currentPage * itemsPerPage, sortedRecords.length)} sur {sortedRecords.length}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            />
            <span className="min-w-[4rem] text-center text-xs text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CallRecordsTable;
